import {
  Box,
  Center,
  Flex,
  Heading,
  Link,
  Skeleton,
  Spacer,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { Address, useAccount, useBalance, useContractReads, useNetwork } from "wagmi";

import DepositTabPanel from "./components/DepositTab";
import RedeemTabPanel from "./components/RedeemTab";
import InvestorBalance from "./components/InvestorBalance";
import FeeList from "./components/FeeList";
import { useEffect, useState } from "react";
import { VaultDetails } from "../types/vault";
import { formatVaultDetails } from "../network/vaults";
import { TokenBalanceDetails } from "../types/tokens";
import { getTokenDescription } from "../config/tokens";
import { formatUnits } from "viem";

export default function VaultInfo() {
  const { vaultAddress } = useParams();
  const [vaultDetails, setVaultDetails] = useState<VaultDetails>();

  const { address } = useAccount();
  const { chain } = useNetwork();

  const {
    data: getVaultData,
    error: getVaultError,
    isError: isGetVaultError,
    isLoading: isGetVaultLoading,
    isSuccess: isGetVaultSuccess,
  } = useContractReads({
    contracts: [
      {
        functionName: "name",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "name",
            outputs: [
              {
                internalType: "string",
                name: "",
                type: "string",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "symbol",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "symbol",
            outputs: [
              {
                internalType: "string",
                name: "",
                type: "string",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "decimals",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "decimals",
            outputs: [
              {
                internalType: "uint8",
                name: "",
                type: "uint8",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "totalSupply",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "totalSupply",
            outputs: [
              {
                internalType: "uint256",
                name: "",
                type: "uint256",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "sourceToken",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "sourceToken",
            outputs: [
              {
                internalType: "contract ERC20",
                name: "",
                type: "address",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "targetToken",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "targetToken",
            outputs: [
              {
                internalType: "contract ERC20",
                name: "",
                type: "address",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "swapper",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "swapper",
            outputs: [
              {
                internalType: "contract ISwapper",
                name: "",
                type: "address",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "minAmountForDCA",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "minAmountForDCA",
            outputs: [
              {
                internalType: "uint256",
                name: "",
                type: "uint256",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "epochLength",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "epochLength",
            outputs: [
              {
                internalType: "uint256",
                name: "",
                type: "uint256",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "currentEpoch",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "currentEpoch",
            outputs: [
              {
                internalType: "uint256",
                name: "",
                type: "uint256",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "entryFeeInBips",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            stateMutability: "view",
            type: "function",
            name: "entryFeeInBips",
            outputs: [
              {
                internalType: "uint256",
                name: "",
                type: "uint256",
              },
            ],
          },
        ],
      },

      {
        functionName: "exitFeeInBips",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "exitFeeInBips",
            outputs: [
              {
                internalType: "uint256",
                name: "",
                type: "uint256",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "executionFeeInBips",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [],
            name: "executionFeeInBips",
            outputs: [
              {
                internalType: "uint256",
                name: "",
                type: "uint256",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ],
      },

      {
        functionName: "balanceOf",
        address: vaultAddress as Address,
        abi: [
          {
            inputs: [
              {
                internalType: "address",
                name: "",
                type: "address",
              },
            ],
            stateMutability: "view",
            type: "function",
            name: "balanceOf",
            outputs: [
              {
                internalType: "uint256",
                name: "",
                type: "uint256",
              },
            ],
          },
        ],
        args: [address as Address],
      },
    ],
    enabled: address != null && vaultAddress != null,
    watch: true,
    select: (data) => formatVaultDetails(vaultAddress as Address, data),
    onSuccess: (data) => setVaultDetails(data),
    onError(error) {
      console.log("useContractReads ERROR", error);
      setVaultDetails(undefined);
    },
  });

  const { data: vaultData } = useBalance({
    address: address,
    token: vaultDetails?.vaultAddress,
    watch: true,
    enabled: address !== undefined && vaultDetails !== undefined,
  });

  const { data: getSrcTokenData } = useBalance({
    address: vaultDetails?.vaultAddress,
    token: vaultDetails?.sourceTokenAddress,
    watch: true,
    enabled: vaultDetails !== undefined && vaultDetails.sourceTokenAddress !== undefined,
  });

  const { data: getTgtTokenData } = useBalance({
    address: vaultDetails?.vaultAddress,
    token: vaultDetails?.targetTokenAddress,
    watch: true,
    enabled: vaultDetails !== undefined && vaultDetails.targetTokenAddress !== undefined,
  });

  return (
    <Flex>
      {/* LEFT COLUMN */}
      {vaultDetails ? (
        <Box maxWidth="60%">
          <Heading size="lg" minH="1em" minW="5em">
            {vaultDetails?.vaultName}
          </Heading>
          <Box fontSize="sm" fontWeight="thin" fontStyle="italic">
            <Text display="inline">{vaultDetails?.vaultSymbol}</Text>
            &nbsp;•&nbsp;
            <Link
              display="inline"
              href={chain?.blockExplorers?.default.url + "/address/" + vaultAddress}
              isExternal
              color="teal.500"
            >
              {vaultAddress}
            </Link>
          </Box>

          <Text mt="1em">
            Deposit {getSrcTokenData?.symbol} into this vault, and it will DCA into {getTgtTokenData?.symbol}.
          </Text>
          {/* <Text>Minimum interval between swaps: {vaultDetails.epochLength.toString()} (TODO: time unit).</Text> */}
          <Text>Minimum interval between swaps: 24 hours.</Text>

          {getTgtTokenData && (
            <Text py="2.5em" style={{ whiteSpace: "pre-line" }}>
              {getTokenDescription(getTgtTokenData?.symbol)}{" "}
            </Text>
          )}

          <Box>
            <Heading size="md">TVL</Heading>

            <Stat>
              {vaultDetails?.totalSupply !== undefined && vaultDetails?.vaultDecimals !== undefined && (
                <StatLabel mt="0.5em">
                  Total Shares Minted (Supply):{" "}
                  {formatUnits(vaultDetails?.totalSupply, Number(vaultDetails?.vaultDecimals))}
                  &nbsp;{vaultDetails?.vaultSymbol}
                </StatLabel>
              )}

              <StatNumber mt="0.25em">
                {getTgtTokenData?.formatted}&nbsp;
                {getTgtTokenData?.symbol}
              </StatNumber>
              <StatNumber>+</StatNumber>
              <StatNumber>
                {getSrcTokenData?.formatted}&nbsp;{getSrcTokenData?.symbol}
              </StatNumber>
            </Stat>
          </Box>

          <FeeList vaultDetails={vaultDetails} />

          {/* TODO: last X transactions */}
        </Box>
      ) : (
        <Skeleton w="60%" />
      )}

      <Spacer />

      {/* RIGHT COLUMN */}
      <Box width="30%">
        {vaultData ? (
          <InvestorBalance
            vaultData={vaultData}
            sourceTokenSymbol={getSrcTokenData?.symbol}
            targetTokenSymbol={getTgtTokenData?.symbol}
          />
        ) : (
          <Skeleton w="100%" h="5em" />
        )}

        <Tabs isFitted variant="soft-rounded" mt="2.5em">
          <TabList borderColor="black">
            <Tab>Deposit</Tab>
            <Tab>Redeem</Tab>
          </TabList>

          <TabPanels>
            <DepositTabPanel vaultInfo={vaultDetails} sourceTokenDetails={getSrcTokenData as TokenBalanceDetails} />
            <RedeemTabPanel
              vaultInfo={vaultDetails}
              sourceTokenDetails={getSrcTokenData as TokenBalanceDetails}
              targetTokenDetails={getTgtTokenData as TokenBalanceDetails}
            />
          </TabPanels>
        </Tabs>
      </Box>
    </Flex>
  );
}
